/**
 * The reseller theme: how a white-label customer's colour reaches this app's styling.
 *
 * Why this is not the file bolify-reseller uses
 * ---------------------------------------------
 * That app themes `--brand-*`, and earns the right to by carrying its own fallbacks
 * (`rgb(var(--brand-primary-rgb, 124 83 252))`) through tailwind.config, index.css and the tool
 * SVGs. This app has a different design system and has already done that work: every accent
 * utility — `primary`, `brand`, `brand-primary`, `brandLinear` — resolves through
 * `--qc-iris-50..900-rgb` (tailwind.config.js), split into a light (`:root`) and a dark (`.dark`)
 * ramp in index.css. So the entire theme here is "override those ten variables", and nothing in
 * the design system has to be replaced to get it.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE — the platform's own theme must never change. A reseller's
 * colour applies ONLY on that reseller's own domain. On any host /branding does not report as a
 * live reseller domain, nothing is injected at all and the app renders the colours it shipped with.
 *
 * It is enforced in one place with one mechanism: a single <style> element. applyResellerTheme()
 * removes it unless `is_white_label` is true, so a cached appSetting from another host, or
 * switching domains in one tab, cannot leak a reseller's colour onto the platform. Nothing is ever
 * written to an element's inline style, so there is no per-property cleanup to get wrong.
 */

const STYLE_ID = "reseller-theme";

type Rgb = [number, number, number];

/**
 * Is this host a live white-label reseller domain?
 *
 * Tolerant of the shapes a boolean column really arrives in over JSON: `true`, `1`, `"1"` and
 * `"true"` mean yes; everything else, including a missing key, means no.
 *
 * Both obvious one-liners are wrong in a way that is hard to see. A bare truthy test reads the
 * STRING "false" as yes, which would paint a reseller's colour onto a platform host. A bare
 * `=== true` silently ignores a backend that serialises the column as 1 — and the symptom of that
 * is exactly "the reseller saved a colour and nothing happened", with no error anywhere.
 */
export function isWhiteLabel(value: unknown): boolean {
  if (value === true || value === 1) return true;
  return typeof value === "string" && ["1", "true"].includes(value.trim().toLowerCase());
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#B8451F" -> [184, 69, 31]. Null for anything that is not a hex colour — never a silent black. */
export function hexToRgb(value: unknown): Rgb | null {
  const raw = String(value ?? "").trim();
  if (!HEX.test(raw)) return null;
  let hex = raw.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
}

/** Relative luminance (WCAG 2.1), used for the contrast ratio the settings screen warns on. */
function luminance([r, g, b]: Rgb): number {
  const [R, G, B] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** WCAG contrast ratio between two hex colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number | null {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) return null;
  const [la, lb] = [luminance(ca), luminance(cb)];
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white text for a surface filled with this colour. */
export function readableTextOn(hex: string): "#FFFFFF" | "#111111" {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#FFFFFF";
  return luminance(rgb) > 0.45 ? "#111111" : "#FFFFFF";
}

export function rgbToHsl([r, g, b]: Rgb): { h: number; s: number; l: number } {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === R ? ((G - B) / d) % 6 : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  h *= 60;
  return { h: h < 0 ? h + 360 : h, s, l };
}

export function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0] :
    h < 120 ? [x, c, 0] :
    h < 180 ? [0, c, x] :
    h < 240 ? [0, x, c] :
    h < 300 ? [x, 0, c] : [c, 0, x];
  return [r, g, b].map((v) => Math.round((v + m) * 255)) as Rgb;
}

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/**
 * The ramp is the reseller's HUE AND SATURATION at the LIGHTNESS the design system already tuned.
 *
 * These two curves are measured off the shipped flame ramp in index.css, so the roles its comments
 * document are preserved whatever colour a reseller picks: 500 is "THE accent", 600 its hover, 400
 * the brighter mark, and 50/100 the tint surfaces. Feeding the shipped flame (#B8451F / #EB663A)
 * back through this reproduces the shipped ramp to within 4/255 on any channel — which is the
 * check that it is a description of the design rather than an invention on top of it.
 *
 * Dark is deliberately NOT the light curve: there 50/100 are dark tint SURFACES (selected row,
 * selection background) rather than near-white tints, and the accent sits brighter so it still
 * reads on ink paper. Using one curve for both is what turns a dark-mode selected row white.
 */
const LIGHT_L = [0.95, 0.90, 0.42, 0.47, 0.58, 0.42, 0.35, 0.29, 0.24, 0.19];
const DARK_L = [0.19, 0.28, 0.63, 0.58, 0.69, 0.58, 0.47, 0.42, 0.35, 0.24];

/**
 * Saturation is not flat across the ramp either, and the two places it matters most are the dark
 * tint surfaces: the shipped dark 50/100 are the accent mixed INTO the ink paper, which drops their
 * saturation to ~0.6x. Reproducing them at full saturation gives a strongly coloured selected row
 * instead of a tinted one. These multipliers are measured the same way as the lightness curves;
 * the result is clamped at 1, so an already-saturated brand colour simply stays where it is.
 */
const LIGHT_S = [0.96, 0.90, 1.0, 0.95, 1.15, 1.0, 1.03, 1.03, 1.04, 1.03];
const DARK_S = [0.59, 0.7, 1.18, 1.15, 1.17, 1.15, 0.95, 1.0, 1.03, 1.04];

/** Corner roundness presets, applied through the --radius-* variables the surfaces already read. */
export const THEME_RADII: { value: string; label: string; css: string }[] = [
  { value: "", label: "Default", css: "" },
  { value: "sharp", label: "Sharp", css: "2px" },
  { value: "rounded", label: "Rounded", css: "10px" },
  { value: "pill", label: "Pill", css: "16px" },
];

/** The ten `--qc-iris-*-rgb` values a colour produces, for one mode. Exported for the preview. */
export function buildRamp(primary: string, mode: "light" | "dark"): Rgb[] {
  const rgb = hexToRgb(primary);
  if (!rgb) return [];
  const { h, s } = rgbToHsl(rgb);
  const [lightness, saturation] = mode === "light" ? [LIGHT_L, LIGHT_S] : [DARK_L, DARK_S];
  return lightness.map((l, i) => hslToRgb(h, Math.min(1, s * saturation[i]), l));
}

export type ResellerTheme = {
  primary_color?: string | null;
  theme_radius?: string | null;
  is_white_label?: unknown;
};

/**
 * Apply (or clear) the reseller's theme.
 *
 * @param settings the /branding response (AppConfigProvider stores it in localStorage.appSetting).
 *                 `is_white_label` decides everything: false or missing and the style element is
 *                 removed, leaving the platform's shipped theme untouched.
 */
export function applyResellerTheme(settings: ResellerTheme | null | undefined): void {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(STYLE_ID);

  if (!settings || !isWhiteLabel(settings.is_white_label)) {
    existing?.remove();
    return;
  }

  const primary = hexToRgb(settings.primary_color) ? String(settings.primary_color).trim() : null;
  const radius = THEME_RADII.find((r) => r.value === settings.theme_radius)?.css || "";
  if (!primary && !radius) {
    existing?.remove();
    return;
  }

  const rules: string[] = [];
  if (primary) {
    const ramp = (mode: "light" | "dark") =>
      buildRamp(primary, mode)
        .map((c, i) => `  --qc-iris-${STEPS[i]}-rgb: ${c.join(" ")};`)
        .join("\n");
    // Same selectors index.css uses. `.dark` is set on <body> (utils/theme-mode-boot), so it is a
    // descendant of :root and its values win inside the app shell exactly as the shipped ones do.
    rules.push(`:root {\n${ramp("light")}\n}`);
    rules.push(`.dark {\n${ramp("dark")}\n}`);
  }
  if (radius) {
    rules.push(
      `:root {\n${["sm", "md", "lg", "xl", "2xl"]
        .map((k) => `  --radius-${k}: ${radius};`)
        .join("\n")}\n}`
    );
  }

  const style = existing instanceof HTMLStyleElement ? existing : document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = rules.join("\n");
  // appendChild MOVES an existing node, which is the point: these rules carry the same specificity
  // as the ones they override, so they only win by being last in the document's cascade order.
  document.head.appendChild(style);
}
